import React, { useCallback, useMemo } from 'react'
import styled from 'styled-components'
import PreviewModule, { getActualModuleOffset } from './PreviewModule'
import Tooltip from '../../components/Tooltip'
import ModuleData, { isSingularModule, isVariableModule, VariableModuleData } from '../../../types/ModuleData'
import Droppable, { DroppableProps } from '../../components/dnd/Droppable'
import { ANIMATION_SCALE } from '../../style'
import { CSSTransition } from 'react-transition-group'
import { useBreakpoint, useInspirationDetail } from '../../store/hooks/uiHooks'
import { useSelector } from '../../store/hooks'
import { reorderArray } from '../../store/actions/configurationActions'
import PreviewSingularModule from './PreviewSingularModule'
import { isDefined } from '../../utils/typeGuards'
import { ConstelaConfiguration, ConstelaConfigurationModule } from '../../../types/ConstelaConfiguration'
import { usePropertyOverlay } from '../../store/hooks/uiExpertHooks'
import { getPreviewData, ResolvedModule } from './previewUtils'

export type LuminaryEditorProps = {
  configuration: ConstelaConfiguration
  updateModules: (
    nextModules: ConstelaConfigurationModule[],
    type: 'insert' | 'move' | 'change',
    affectedIndices: number[]
  ) => unknown
  canInsertModule: (moduleId: string) => boolean
  scale: number
  availableModules: ModuleData[]
  animate?: 'show' | 'hide'
}

const SteleWrap = styled.div<{ width: number }>`
  width: ${({ width }) => width}px;
`

const ModuleWrap = styled.div<{
  index: number
}>`
  --index: ${({ index }) => index};
  transition-duration: ${800 * ANIMATION_SCALE}ms;
  transition-delay: ${({ index }) => 200 * index * ANIMATION_SCALE}ms;

  .enter,
  .enter-active & {
    transform: translateY(-${({ index }) => (index ? '100%' : '50px')});
    opacity: ${({ index }) => (index ? 0 : 1)};
    transition-duration: 0ms;
    transition-delay: 0ms;
  }

  .enter-done & {
    transition-property: opacity, transform;
    transform: translateY(0px);
    opacity: 1;
  }
`

const getTooltipLeft = (systemWidth: number, module: ModuleData | undefined, scale: number): number => {
  if (!module || !('width' in module)) {
    return 0
  }
  const { left } = getActualModuleOffset(systemWidth, module, scale)
  return Math.round(left + module.width * scale)
}

const LuminaryEditor: React.FC<LuminaryEditorProps> = ({
  configuration,
  scale,
  updateModules,
  availableModules,
  animate,
  canInsertModule
}) => {
  const { detailModule, showDetail, closeDetail } = useInspirationDetail()

  const { lightingHead, supportingColumn, singularModules, variableModules } = useMemo(
    () => getPreviewData(configuration, availableModules, configuration.type === 'expert'),
    [availableModules, configuration]
  )

  const toggleModuleDetail = useCallback(
    (module?: ModuleData | ResolvedModule<ModuleData>) => {
      if (!module) {
        return
      }
      if (detailModule?.id === module.id) {
        closeDetail()
      } else {
        showDetail(module.id)
      }
    },
    [closeDetail, detailModule?.id, showDetail]
  )

  const { openModuleProperties } = usePropertyOverlay()

  const enableDragDrop = useBreakpoint('tablet')
  const acceptsItem = useCallback<Exclude<DroppableProps['acceptsItem'], undefined>>(
    ({ item, droppableSourceId, droppableTargetId }) => {
      if (droppableSourceId === droppableTargetId) {
        // If the module is just moved around inside the luminary don't bother
        return true
      }
      return canInsertModule(item)
    },
    [canInsertModule]
  )

  const widthmm = useSelector((state) => state.data.system.width)
  const [highlightInsetLeft, highlightInsetRight] = useMemo(() => {
    const insets = availableModules
      .filter(isVariableModule)
      .map((module) => getActualModuleOffset(widthmm, module, scale))
    return [Math.min(...insets.map((x) => x.left)), Math.min(...insets.map((x) => x.right))]
  }, [availableModules, scale, widthmm])

  const currentExpertModule = useSelector((state) =>
    state.ui.mode === 'expert' ? state.ui.expert.showProperties?.module : undefined
  )
  const variableModuleData = useMemo<
    (ResolvedModule<VariableModuleData> & {
      active: boolean
      onTooltipClick?: () => void
    })[]
  >(
    () =>
      variableModules.map((module) => ({
        ...module,
        onTooltipClick:
          configuration.type === 'inspiration'
            ? () => toggleModuleDetail(module)
            : 'propertyMenu' in module.data
            ? () => openModuleProperties(module.index)
            : undefined,
        active: currentExpertModule === module.index
      })),
    [configuration.type, currentExpertModule, openModuleProperties, toggleModuleDetail, variableModules]
  )
  const updateVariableModules = useCallback(
    (item: string, from: number | undefined, to: number | undefined) => {
      const nonVariableModules = configuration.modules.filter(({ id }) => !variableModuleData.some((x) => x.id === id))
      const variableModules2 = configuration.modules.filter(({ id }) => variableModuleData.some((x) => x.id === id))
      const nextModules = reorderArray(variableModules2, { id: item }, from, to).concat(nonVariableModules)
      updateModules(nextModules, from ? 'move' : 'insert', [from, to].filter(isDefined))
    },
    [configuration.modules, updateModules, variableModuleData]
  )

  const setSingularModule = useCallback(
    (id: string, nextSelected: boolean) => {
      updateModules(configuration.modules.filter((x) => x.id !== id).concat(nextSelected ? [{ id }] : []), 'change', [
        configuration.modules.length - 1
      ])
    },
    [configuration.modules, updateModules]
  )

  const openLightingHead = useCallback(() => toggleModuleDetail(lightingHead?.data), [lightingHead, toggleModuleDetail])

  return (
    <CSSTransition in={animate !== 'hide'} timeout={0}>
      <SteleWrap width={scale * widthmm}>
        <ModuleWrap index={configuration.modules.length + 1}>
          <Tooltip
            top="50%"
            left={getTooltipLeft(widthmm, lightingHead?.data, scale) + 'px'}
            title={lightingHead?.data.title}
            delay={(singularModules.length + configuration.modules.length + 1) * 200 + 600}
            onClick={configuration.type === 'inspiration' ? openLightingHead : undefined}
            data-cs-ignore-close={
              configuration.type === 'expert' && openLightingHead
                ? 'moduleProperties'
                : configuration.type === 'inspiration'
                ? 'moduleDetail'
                : ''
            }
          >
            <PreviewModule module={lightingHead?.data} scale={scale} />
          </Tooltip>
        </ModuleWrap>
        <Droppable
          items={variableModuleData.map((x) => x.id)}
          disabled={!enableDragDrop}
          type="module"
          updateItems={updateVariableModules}
          acceptsItem={acceptsItem}
          highlightInsetLeft={highlightInsetLeft}
          highlightInsetRight={highlightInsetRight}
          inverted
        >
          {(item, i, items, provided) => {
            const { data: module, onTooltipClick, active } = variableModuleData[i]
            return (
              <ModuleWrap index={items.length - i}>
                <Tooltip
                  top="50%"
                  left={getTooltipLeft(widthmm, module, scale) + 'px'}
                  title={module?.title}
                  delay={(singularModules.length + configuration.modules.length - i) * 200 + 600}
                  icon={configuration.type === 'expert' && onTooltipClick ? 'cogwheel' : undefined}
                  $active={active}
                  data-cs-ignore-close={
                    configuration.type === 'expert' && onTooltipClick
                      ? 'moduleProperties'
                      : configuration.type === 'inspiration'
                      ? 'moduleDetail'
                      : ''
                  }
                  onClick={onTooltipClick}
                >
                  <PreviewModule module={module} scale={scale} {...provided.dragHandleProps} />
                </Tooltip>
              </ModuleWrap>
            )
          }}
        </Droppable>
        <ModuleWrap index={0}>
          {availableModules.filter(isSingularModule).map((module) => (
            <PreviewSingularModule
              systemWidth={widthmm}
              module={module}
              scale={scale}
              key={'module-' + module.id}
              selected={singularModules.some((x) => x.id === module.id)}
              setSelected={setSingularModule}
              disabled={!enableDragDrop}
              delay={(singularModules.length - singularModules.findIndex((x) => x.id === module.id)) * 200 + 600}
              onTooltipClick={toggleModuleDetail}
              ignoreClose={
                configuration.type === 'expert' && toggleModuleDetail
                  ? 'moduleProperties'
                  : configuration.type === 'inspiration'
                  ? 'moduleDetail'
                  : ''
              }
            />
          ))}
          <PreviewModule module={supportingColumn?.data} scale={scale} />
        </ModuleWrap>
      </SteleWrap>
    </CSSTransition>
  )
}

export default LuminaryEditor
